import React, { useState } from "react";
import { useAppSelector } from "../../../../../redux/store";
import { Message, MessageSections } from "../../../../components/message/message";
import { Grid, Skeleton } from "@mui/material";
import BarekamInput from "../../../../components/input";
import { BarekamSelect } from "../../../../components/select/select";
import { BarekamMenuItem, SelectedBarekamMenuItem } from "../../../../components/select/styled/orders";
import moment from "moment";
import { Recipient } from "../../../../shared/models/user/recipient/recipient";
import { BarekamAttachment } from "../../../../components/attachment/attachment";
import BarekamDateInput from "../../../../components/input/barekam-date-input";
import { RecipientAddress } from "../../recipients/recipient-address/recipient-address";
import { StandardDialog } from "../../../../components/dialogs/standard-dialog/standard-dialog";
import { EditAddress } from "../../user-addresses/edit-address/edit-address";
import { UserAddress } from "../../../../shared/models/user/user-address";
import { getMessage } from "../../../../shared/helper-functions";

export const RecipientInputs = ({ recipient, onChange, onAddressChange, errors }: UserInputsProps) => {
    const [fileInfo, setFileInfo] = useState(recipient?.document);
    const [addressToEdit, setAddressToEdit] = useState<UserAddress | undefined>();

    const { matchesPhone } = useAppSelector(state => state.deviceReducer);
    const { recipientDocumentTypes } = useAppSelector(state => state.staticDataReducer);

    const { messageCache, languageId } = useAppSelector(state => state.messagesReducer);

    async function handleFileSelect(file: File | undefined) {
        setFileInfo(file);
        onChange(file || '', 'document');
    }

    const handleEditAddressClose = (withChange: boolean, address?: UserAddress) => {
        setAddressToEdit(undefined);

        if (withChange && address) {
            onAddressChange({
                country: address.country,
                province: address.province,
                city: address.city,
                region: address.region,
                street: address.street,
                building: address.building,
                entrance: address.entrance,
                floor: address.floor,
                apartment: address.apartment,
                zipCode: address.zipCode
            } as UserAddress)
        }
    };

    if (recipient) {
        return (
            <>
                <Grid item xs={12}>
                    <BarekamSelect
                        variant={'outlined'}
                        defaultValue={''}
                        label={getMessage('document_type', MessageSections.USER, messageCache, languageId)}
                        value={recipient.documentType?.recipientDocumentTypeId}
                        onChange={(event) => onChange(recipientDocumentTypes.find(type => type.recipientDocumentTypeId === event.target.value), 'documentType')}
                        type={"standard"}
                        required
                        error={!!errors['documentType']}
                        errorMessage={errors['recipient'] && getMessage(errors['documentType'], MessageSections.USER, messageCache, languageId)}>
                        {recipientDocumentTypes
                            .filter(type => type.name !== 'tid')
                            .map(type => {
                                if (type.recipientDocumentTypeId === recipient.documentType?.recipientDocumentTypeId) {
                                    return (
                                        <SelectedBarekamMenuItem value={type.recipientDocumentTypeId}
                                            key={type.name}>
                                            <Message messageKey={type.name} sectionId={MessageSections.USER} />
                                        </SelectedBarekamMenuItem>
                                    )
                                } else {
                                    return (
                                        <BarekamMenuItem value={type.recipientDocumentTypeId}
                                            key={type.name}>
                                            <Message messageKey={type.name} sectionId={MessageSections.USER} />
                                        </BarekamMenuItem>
                                    );
                                }
                            })}
                    </BarekamSelect>
                </Grid>

                <Grid item xs={matchesPhone ? 12 : 6}>
                    <BarekamInput customInputVariant={"standard"}
                        placeholder={getMessage('passport_id', MessageSections.USER, messageCache, languageId)}
                        value={recipient.passportOrIdSerialNumber}
                        onChange={(event) => onChange(event.target.value, 'passportOrIdSerialNumber')}
                        width={'100%'}
                        error={!!errors['passportOrIdSerialNumber']}
                        errorMessage={errors['passportOrIdSerialNumber'] && getMessage(errors['passportOrIdSerialNumber'], MessageSections.USER, messageCache, languageId)}
                        required />
                </Grid>

                <Grid item xs={matchesPhone ? 12 : 6}>
                    <BarekamDateInput views={['year', 'month', 'day']}
                        value={recipient.documentExpirationDate ? moment(recipient.documentExpirationDate) : null}
                        onChange={(event) => onChange(event && event.format('YYYY-MM-DD'), 'documentExpirationDate')}
                        label={getMessage('passport_expire_date', MessageSections.USER, messageCache, languageId)}
                        errorMessage={errors['documentExpirationDate'] && getMessage(errors['documentExpirationDate'], MessageSections.USER, messageCache, languageId)}
                        required
                        disablePast
                    />
                </Grid>

                <Grid item xs={12}>
                    <RecipientAddress recipient={recipient} setAddressToEdit={address => setAddressToEdit(address)} />
                </Grid>

                <Grid item xs={12}>
                    <BarekamAttachment titleLabel={<Message messageKey={'passport_photo'} sectionId={MessageSections.USER} />}
                        messageSection={MessageSections.USER}
                        value={fileInfo}
                        isSingle={true}
                        isReadonly={false}
                        onValueChange={(data: File) => handleFileSelect(data)}
                        errorMessage={errors['recipient'] && getMessage(errors['document'], MessageSections.USER, messageCache, languageId)} />
                </Grid>

                <StandardDialog headerLabel={<Message messageKey={'edit_address'} sectionId={MessageSections.USER} />}
                    open={!!addressToEdit}>
                    <EditAddress address={addressToEdit || {} as UserAddress}
                        onSave={handleEditAddressClose}
                        isRecipientAddress />
                </StandardDialog>
            </>
        )
    } else {
        return (
            <>
                <Skeleton width={'50%'} height={'5.5rem'} >
                </Skeleton>

                <div style={{ display: 'flex' }}>
                    <Skeleton width={'25%'} height={'5.5rem'}>
                    </Skeleton>

                    <Skeleton width={'25%'} height={'5.5rem'}>
                    </Skeleton>
                </div>
            </>
        )
    }
}

interface UserInputsProps {
    recipient?: Recipient,
    onChange: (value: any, field: string) => void,
    onAddressChange: (address: UserAddress) => void,
    errors: { [key: string]: string }
}
